<template>
  <div class="container mx-auto p-4">
    <page-header title="Payment Event Logs" subtitle="Incoming payment events processed by the system." />

    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />
      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :columns="columns"
      :data-handler="getEventLogs"
      row-class="hover:bg-slate-50"
      :per-page="15"
    />
  </div>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import PageHeader from '../components/PageHeader.vue';
import Grid from '../components/grid/Grid.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import SessionUserIcon from '../components/SessionUserIcon.vue';
import PaymentEventLogListActions from '../components/PaymentEventLogListActions.vue';
import { apiDomain } from '../composables/useConstants';
import { formatDateTime } from '../utils/formatters';
import { elipsis } from '../utils/elipsis';

const router = useRouter();
const gridRef = ref(null);
const search = useRouteQuery('search', null);

function reloadGrid(updateQuery = {}) {
  gridRef.value?.reload(updateQuery);
}

watchDebounced(
  search,
  () => reloadGrid({ offset: 0 }),
  { debounce: 500, maxWait: 1000 }
);

async function getEventLogs({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };
  const params = new URLSearchParams(data);
  const { data: result } = await useFetch(`${apiDomain}/api/payment-event-log?${params}`).get().json();
  return {
    total: result.value?.total ?? 0,
    rows: result.value?.items ?? []
  };
}

const columns = [
  {
    title: 'ID',
    value: row => elipsis(row.id, 24),
    class: 'text-xs font-mono cursor-pointer',
    sortable: true,
    click: row => router.push(`/payment-event-log/${row.id}`)
  },
  {
    title: 'Event Type',
    prop: 'eventType',
    class: 'cursor-pointer',
    sortable: true,
    click: row => router.push(`/payment-event-log/${row.id}`)
  },
  {
    title: 'User',
    prop: 'userId',
    class: 'cursor-pointer !py-0',
    component: markRaw(SessionUserIcon),
    click: row => row.userId && router.push(`/user/${row.userId}`)
  },
  {
    title: 'Created At',
    value: row => formatDateTime(row.createdAt),
    class: 'whitespace-nowrap text-xs cursor-pointer',
    sortable: true,
    click: row => router.push(`/payment-event-log/${row.id}`)
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(PaymentEventLogListActions)
  }
];
</script>
